import React from 'react';
import { cn } from '@/lib/utils';

interface OptimizedImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
  className?: string;
  priority?: boolean;
  sizes?: string;
  width?: string | number;
  height?: string | number;
}

const getWebPSource = (src: string): string => {
  // Only attempt WebP conversion for uploaded images, not external URLs
  if (src.includes('/lovable-uploads/') && !src.includes('.webp')) {
    const extension = src.split('.').pop();
    if (extension && ['png', 'jpg', 'jpeg'].includes(extension.toLowerCase())) {
      return src.replace(/\.(png|jpg|jpeg)$/i, '.webp');
    }
  }
  return src;
};

export const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  alt,
  className,
  priority = false,
  sizes,
  width,
  height,
  ...props
}) => {
  const webPSrc = getWebPSource(src);
  const hasModernFormat = webPSrc !== src;

  // For better compatibility, always use regular img tag but with optimization attributes
  return (
    <img
      src={src}
      alt={alt}
      loading={priority ? 'eager' : 'lazy'}
      decoding="async"
      sizes={sizes}
      width={width}
      height={height}
      className={cn('transition-opacity duration-300', className)}
      style={{
        objectFit: 'contain',
        objectPosition: 'center',
      }}
      onError={(e) => {
        // Fallback for failed images
        console.warn('Image failed to load:', src);
        e.currentTarget.style.display = 'block';
      }}
      {...props}
    />
  );
};